/**
 * Firebase Error Handler
 * Provides utilities to handle Firebase errors gracefully
 */

/**
 * Check if an error is a Firebase permission error
 */
export function isFirebasePermissionError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes('Missing or insufficient permissions') ||
      error.message.includes('permission-denied') ||
      (error as any).code === 'permission-denied'
    );
  }
  return false;
}

/**
 * Log Firebase errors appropriately
 * Permission errors are logged as warnings, others as errors
 */
export function logFirebaseError(error: unknown, context: string): void {
  if (isFirebasePermissionError(error)) {
    console.warn(`Firebase permission denied in ${context}. This is expected for some users.`);
  } else {
    console.error(`Firebase error in ${context}:`, error);
  }
}

/**
 * Create a safe Firebase error handler that doesn't log permission errors as errors
 */
export function createSafeFirebaseErrorHandler(context: string) {
  return (error: unknown) => {
    logFirebaseError(error, context);
  };
}

/**
 * Suppress console errors for Firebase permission errors globally
 * This prevents them from being sent to error tracking services
 */
export function suppressFirebasePermissionErrors(): void {
  const originalError = console.error;
  
  console.error = (...args: any[]) => {
    // Check if any argument contains Firebase permission error
    const hasPermissionError = args.some(arg => {
      if (typeof arg === 'string') {
        return arg.includes('Missing or insufficient permissions') || 
               arg.includes('permission-denied');
      }
      if (arg instanceof Error) {
        return isFirebasePermissionError(arg);
      }
      return false;
    });
    
    // If it's a permission error, log as warning instead
    if (hasPermissionError) {
      console.warn('Firebase permission denied:', ...args);
      return;
    }
    
    // Otherwise, call original console.error
    originalError.apply(console, args);
  };
}